import { SafetyPayload } from "../payloadTypes";
import { SafetyData, SafetyIndicator, FactRow } from "../types";
import {
  isFiniteInRange,
  formatPercent,
  formatOneDecimal,
  warnOmitted,
  withHeroImageParams,
} from "./neighbourhoodHelpers";
import config from "@Config/index";

const heroImage = withHeroImageParams(`${config.IMAGE_STATIC_URL}/neighbourhood-assets/safety.png`);

const KEY_PREFIX = "propertypage.components.neighbourhood.safety";

/**
 * Transforms the backend SafetyPayload into the view-model SafetyData.
 *
 * Requirements: 3.1-3.19, 6.1, 6.3, 10.9
 */
export const transformSafety = (
  safety: SafetyPayload | null | undefined,
  cityName: string | undefined,
): SafetyData => {
  const facts: FactRow[] = [];
  const indicators: SafetyIndicator[] = [];

  // ─── Violent Crime Row ──────────────────────────────────────────────────────
  const violentRaw = safety?.violent_crime_percent_difference;
  const violentValid = typeof violentRaw === "number" && Number.isFinite(violentRaw);

  if (violentValid && cityName) {
    const p = formatPercent(violentRaw);
    if (p !== "0") {
      if (violentRaw > 0) {
        facts.push({
          key: `${KEY_PREFIX}.fact.violentCrime.above`,
          params: { percent: p, city: cityName },
          trend: "up",
        });
      } else {
        facts.push({
          key: `${KEY_PREFIX}.fact.violentCrime.below`,
          params: { percent: p, city: cityName },
          trend: "down",
        });
      }
    } else {
      warnOmitted("violent crime row", "safety.violent_crime_percent_difference (rounds to zero)");
    }
  } else {
    if (!violentValid && violentRaw != null) {
      warnOmitted("violent crime row", "safety.violent_crime_percent_difference");
    }
    if (violentValid && !cityName) {
      warnOmitted("violent crime row", "cityName (absent)");
    }
  }

  // ─── Property Crime Row ─────────────────────────────────────────────────────
  const propertyRaw = safety?.property_crime_percent_difference;
  const propertyValid = typeof propertyRaw === "number" && Number.isFinite(propertyRaw);

  if (propertyValid) {
    const p = formatPercent(propertyRaw);
    if (p !== "0") {
      if (propertyRaw > 0) {
        facts.push({
          key: `${KEY_PREFIX}.fact.propertyCrime.higher`,
          trend: "up",
        });
      } else {
        facts.push({
          key: `${KEY_PREFIX}.fact.propertyCrime.lower`,
          trend: "down",
        });
      }
    } else {
      warnOmitted(
        "property crime row",
        "safety.property_crime_percent_difference (rounds to zero)",
      );
    }
  } else if (propertyRaw != null) {
    warnOmitted("property crime row", "safety.property_crime_percent_difference");
  }

  // ─── Police Officers Row ────────────────────────────────────────────────────
  const officersRaw = safety?.police_officers_per_1000?.officers_per_1000;

  if (isFiniteInRange(officersRaw, 0, 100) && officersRaw > 0) {
    facts.push({
      key: `${KEY_PREFIX}.fact.policeOfficers`,
      params: { officers: formatOneDecimal(officersRaw) },
      trend: "neutral",
    });
  } else if (officersRaw != null) {
    warnOmitted("police officers row", "safety.police_officers_per_1000.officers_per_1000");
  }

  // ─── Daytime Badge ──────────────────────────────────────────────────────────
  const daySafety = safety?.day_safety;

  if (isFiniteInRange(daySafety, 0, 100)) {
    let level: "safe" | "moderate" | "unsafe";
    if (daySafety >= 70) {
      level = "safe";
    } else if (daySafety >= 40) {
      level = "moderate";
    } else {
      level = "unsafe";
    }

    let valueKey: string;
    if (level === "safe") {
      valueKey = `${KEY_PREFIX}.badge.value.safe`;
    } else if (level === "moderate") {
      valueKey = `${KEY_PREFIX}.badge.value.moderate`;
    } else {
      valueKey = `${KEY_PREFIX}.badge.value.risk`;
    }

    indicators.push({
      labelKey: `${KEY_PREFIX}.badge.daytime`,
      value: { kind: "key", key: valueKey },
      level,
    });
  } else if (daySafety != null) {
    warnOmitted("daytime badge", "safety.day_safety");
  }

  // ─── Nighttime Badge ────────────────────────────────────────────────────────
  const nightSafety = safety?.night_safety;

  if (isFiniteInRange(nightSafety, 0, 100)) {
    let level: "safe" | "moderate" | "unsafe";
    if (nightSafety >= 60) {
      level = "safe";
    } else if (nightSafety >= 30) {
      level = "moderate";
    } else {
      level = "unsafe";
    }

    let valueKey: string;
    if (level === "safe") {
      valueKey = `${KEY_PREFIX}.badge.value.safe`;
    } else if (level === "moderate") {
      valueKey = `${KEY_PREFIX}.badge.value.moderate`;
    } else {
      valueKey = `${KEY_PREFIX}.badge.value.risk`;
    }

    indicators.push({
      labelKey: `${KEY_PREFIX}.badge.nighttime`,
      value: { kind: "key", key: valueKey },
      level,
    });
  } else if (nightSafety != null) {
    warnOmitted("nighttime badge", "safety.night_safety");
  }

  // ─── Hero (based on safety_index_percent_difference) ─────────────────────────
  const safetyPercentDiff = safety?.safety_index_percent_difference;
  const safetyIndex = safety?.safety_index;

  let percentageDiff: string | undefined;
  let direction: "safer" | "less safe" | undefined;
  let heroLabelKey: string | undefined;
  let safetyScore: string | undefined;

  if (safetyPercentDiff != null && Number.isFinite(safetyPercentDiff) && safetyPercentDiff > 0) {
    // Positive: show "{x}% safer than the top cities average"
    const absVal = Math.abs(Math.round(safetyPercentDiff));
    if (absVal > 0) {
      percentageDiff = `${absVal}%`;
      direction = "safer";
      heroLabelKey = `${KEY_PREFIX}.hero.label.comparison`;
    }
  } else if (
    safetyPercentDiff != null &&
    Number.isFinite(safetyPercentDiff) &&
    safetyPercentDiff < 0
  ) {
    // Negative: show safety_index as a score out of 100
    if (safetyIndex != null && Number.isFinite(safetyIndex)) {
      // Normalize to 0-100 if needed
      let normalizedScore = Math.round(safetyIndex);
      if (normalizedScore > 100) {
        normalizedScore = Math.round((safetyIndex / normalizedScore) * 100);
      }
      if (normalizedScore < 0) normalizedScore = 0;
      if (normalizedScore > 100) normalizedScore = 100;
      safetyScore = `${normalizedScore}/100`;
      heroLabelKey = `${KEY_PREFIX}.hero.label`;
    }
  }

  // ─── Assemble SafetyData ────────────────────────────────────────────────────
  return {
    heroImage,
    ...(percentageDiff !== undefined && { percentageDiff }),
    ...(direction !== undefined && { direction }),
    ...(heroLabelKey !== undefined && { heroLabelKey }),
    ...(safetyScore !== undefined && { safetyScore }),
    comparisonLabelKey: `${KEY_PREFIX}.hero.label`,
    indicators,
    facts,
    sourceKey: `${KEY_PREFIX}.source`,
  };
};

export default transformSafety;
